/**
 * SourceChips — the personal inbox's segmented provider filter (§11.4).
 *
 *   ┌──────────┬──────────┬───────────┐
 *   │ All mail │ ● Gmail  │ ● Outlook │     on #F4F2EE, active pill white
 *   └──────────┴──────────┴───────────┘
 *
 * The dot is a HEALTH signal, not decoration: green when that provider's
 * connection is live, amber when it needs reconnecting, and the chip is absent
 * entirely when the provider was never connected. A filter for a mailbox you do
 * not have is a control that can only ever return nothing.
 *
 * This replaces a `provider` dropdown buried in the Filters popover. Source is
 * the filter people reach for most on a merged inbox — "where did this come
 * from" is the first question a two-account inbox raises — and a popover made
 * it a three-click answer.
 */
import React from 'react';

/** Live vs needs-attention. Both are "connected"; only one is working. */
const DOT = {
    healthy: 'bg-[#00A63E]',
    degraded: 'bg-amber-500',
};

function Chip({ active, onClick, dot, children, testId }) {
    return (
        <button
            type="button"
            onClick={onClick}
            aria-pressed={active}
            data-testid={testId}
            className={`inline-flex items-center gap-1.5 h-7 px-3 rounded-[7px] text-[12px]
                        whitespace-nowrap transition-colors ${
                            active
                                ? 'bg-white text-stone-900 shadow-sm font-medium'
                                : 'text-stone-600 hover:text-stone-900'
                        }`}
        >
            {dot && <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${DOT[dot]}`} />}
            {children}
        </button>
    );
}

/**
 * @param {string|undefined} value     Active provider, or undefined for all of them.
 * @param {object} sources             `{ gmail: 'healthy'|'degraded'|null, outlook: ... }`
 *                                     — null/absent means never connected.
 */
export default function SourceChips({ value, onChange, sources = {} }) {
    const connected = ['gmail', 'outlook'].filter((k) => sources[k]);
    // With nothing connected the whole control is noise: there is no second
    // source to distinguish, and "All mail" alone is not a choice.
    if (!connected.length) return null;

    return (
        <div
            className="flex items-center gap-0.5 p-0.5 rounded-[9px] bg-[#F4F2EE] flex-shrink-0"
            role="group"
            aria-label="Filter by source"
            data-testid="inbox-source-chips"
        >
            {/* "All providers", not "All mail".
                This row selects a PROVIDER; the folder rail's "All mail"
                selects a FOLDER. Both said "All mail" on the same screen —
                same words, unrelated axes, and no way to tell which one you
                were changing (#706). The rail keeps the name, because it
                genuinely means all mail. */}
            <Chip
                active={!value}
                onClick={() => onChange(undefined)}
                testId="inbox-source-all"
            >
                All providers
            </Chip>
            {connected.map((key) => (
                <Chip
                    key={key}
                    active={value === key}
                    onClick={() => onChange(value === key ? undefined : key)}
                    dot={sources[key]}
                    testId={`inbox-source-${key}`}
                >
                    {key === 'gmail' ? 'Gmail' : 'Outlook'}
                </Chip>
            ))}
        </div>
    );
}
